/**
 * checkCounterInvariants — implémentation PURE de l'oracle #1672 (SPEC-1..8 / INV-1..7).
 *
 * Cette fonction ne dépend d'aucun manager : elle prend un instantané des surfaces d'affichage
 * (`CounterSurfaces`) + éventuellement le store SmartLabel, et renvoie la liste des VIOLATIONS
 * d'invariant détectées (vide = tout est cohérent).
 *
 * DEUX usages, un seul code :
 *   - **assertion de test** (Étape 3) : `expect(checkCounterInvariants(...)).toEqual([])` ;
 *   - **instrumentation prod** (Étape 6, recette manuelle #1672) : `MailManager._checkCounterInvariantsAndWarn`
 *     construit un `CounterSurfaces` depuis les DERNIERS payloads réellement émis (welcome / navigation /
 *     datacards) et émet un `logger.warn(LOGS.Counters1672, …)` par violation — activable via `FloatingLogSettings`.
 *     Le module est donc **prod-atteignable** (hors `__tests__/`, inclus dans le build lib).
 *
 * Chaque violation renvoie `{ code, message }` où `code` référence l'invariant (`INV-1`..`INV-7`)
 * et la SPEC associée. Cf. `Doc/#1672-CompteurMails/11_SPEC_COMPORTEMENT.md`, `04_MATRICE_DECLENCHEURS.md`
 * et `07_LOGS_INSTRUMENTATION.md` (correspondance INV-* ↔ warnings W-*).
 */
import type { SmartLabelEntry } from "../../customView/types/customViews";
import type { SmartLabel } from "../types/mail";
import { ACTIVE_SMART_LABELS } from "../types/mail";

export type InvariantViolation = { code: string; message: string };

/**
 * Une "surface" = un point d'affichage d'une valeur logique de compteur. `undefined` = surface
 * jamais alimentée (⇒ restée sur son état antérieur / vide côté FE). En prod on lit le DERNIER
 * payload réellement émis par le bus pour chaque canal (cf. `MailManager` last-emitted).
 */
export type CounterSurfaces = {
    /** Welcome X = non-lus Inbox (dashboard-welcome-messages-updated.newMailsCount). */
    welcomeX?: number;
    /** Welcome Y = Focus (dashboard-welcome-messages-updated.importantMailsCount). */
    welcomeY?: number;
    /** Nav Réception = navigation-updated.generalNavItems[Inbox].itemCount. */
    navInbox?: number;
    /** Nav Focus = navigation-updated.smartlabelNavItems[Focus].itemCount. */
    navFocus?: number;
    /** DataCard Focus = dashboard-favorite-views-updated[viewId=Focus].mailCount. */
    dataCardFocus?: number;
    /** Compteurs par SmartLabel (navigation-updated). */
    navSmartLabels: Partial<Record<SmartLabel, number>>;
    /** Répartition Focus (navigation-updated Focus.categoryRepartition). */
    focusRepartition?: { categoryId: string; percentage: number }[];
};

export type CheckOptions = {
    /**
     * Store SmartLabel (pour INV-4 exclusivité). Si fourni, vérifie qu'aucun `internetMessageId`
     * n'apparaît dans > 1 label et que Σ(compteurs) == nb d'ids distincts.
     */
    store?: Partial<Record<SmartLabel, SmartLabelEntry[]>>;
    /**
     * Nombre de mails Focus réellement connus/résolus servant de base à la répartition (INV-6).
     * Si fourni avec `surfaces.navFocus`, vérifie l'égalité (compteur == contenu connu).
     */
    focusRepartitionBaseCount?: number;
    /**
     * Historique des valeurs Focus émises via navigation-updated (INV-7). Détecte un flip-flop :
     * une valeur émise, puis une autre, puis un retour figé sur une valeur périmée ≠ dernière connue.
     */
    focusEmitHistory?: number[];
    /** Dernière valeur Focus « vraie » attendue (état convergé connu), pour INV-7. */
    expectedConvergedFocus?: number;
};

function isNonNegativeInt(n: number | undefined): boolean {
    return (
        typeof n === "number" &&
        Number.isFinite(n) &&
        n >= 0 &&
        Number.isInteger(n)
    );
}

/** Toutes les valeurs définies d'un tableau sont-elles égales ? */
function allEqualDefined(values: (number | undefined)[]): boolean {
    const defined = values.filter((v): v is number => v !== undefined);
    if (defined.length === 0) return true;
    return defined.every((v) => v === defined[0]);
}

export function checkCounterInvariants(
    surfaces: CounterSurfaces,
    options: CheckOptions = {},
): InvariantViolation[] {
    const violations: InvariantViolation[] = [];

    // ── INV-1 / SPEC-1 — Cohérence Focus : Welcome Y == Nav Focus == DataCard Focus. ──
    // Post-convergence, ces 3 surfaces d'UNE MÊME valeur logique doivent toutes être définies ET égales.
    // Un fan-out partiel (surface jamais émise alors que les autres l'ont été) = divergence.
    {
        const trio = [
            surfaces.welcomeY,
            surfaces.navFocus,
            surfaces.dataCardFocus,
        ];
        const anyDefined = trio.some((v) => v !== undefined);
        const allDefined = trio.every((v) => v !== undefined);
        if (anyDefined && !allDefined) {
            violations.push({
                code: "INV-1",
                message:
                    `Focus fan-out partiel : welcomeY=${surfaces.welcomeY}, navFocus=${surfaces.navFocus}, ` +
                    `dataCardFocus=${surfaces.dataCardFocus} — une surface n'a pas reçu la valeur.`,
            });
        } else if (!allEqualDefined(trio)) {
            violations.push({
                code: "INV-1",
                message:
                    `Focus divergent entre surfaces : welcomeY=${surfaces.welcomeY}, navFocus=${surfaces.navFocus}, ` +
                    `dataCardFocus=${surfaces.dataCardFocus}.`,
            });
        }
    }

    // ── INV-5 / SPEC-2 — Cohérence non-lus Inbox : Welcome X == Nav Réception. ──
    {
        const pair = [surfaces.welcomeX, surfaces.navInbox];
        const anyDefined = pair.some((v) => v !== undefined);
        const allDefined = pair.every((v) => v !== undefined);
        if (anyDefined && !allDefined) {
            violations.push({
                code: "INV-5",
                message: `Inbox fan-out partiel : welcomeX=${surfaces.welcomeX}, navInbox=${surfaces.navInbox}.`,
            });
        } else if (!allEqualDefined(pair)) {
            violations.push({
                code: "INV-5",
                message: `Non-lus Inbox divergent : welcomeX=${surfaces.welcomeX}, navInbox=${surfaces.navInbox}.`,
            });
        }
    }

    // ── INV-4 / SPEC-4 — Exclusivité SmartLabel (pas de double-compte). ──
    if (options.store) {
        const labelOfId = new Map<string, SmartLabel[]>();
        let sumCounts = 0;
        for (const label of ACTIVE_SMART_LABELS) {
            const entries = options.store[label] ?? [];
            sumCounts += entries.length;
            for (const entry of entries) {
                const arr = labelOfId.get(entry.id) ?? [];
                arr.push(label);
                labelOfId.set(entry.id, arr);
            }
        }
        const duplicated = [...labelOfId.entries()].filter(
            ([, labels]) => labels.length > 1,
        );
        if (duplicated.length > 0) {
            const detail = duplicated
                .map(([id, labels]) => `${id}∈{${labels.join(",")}}`)
                .join("; ");
            violations.push({
                code: "INV-4",
                message: `Double-compte SmartLabel (un mail dans ≥ 2 labels) : ${detail}.`,
            });
        }
        // Σ(compteurs) doit == nb d'ids distincts (corollaire de l'exclusivité).
        if (sumCounts !== labelOfId.size) {
            violations.push({
                code: "INV-4",
                message: `Σ(compteurs SmartLabel)=${sumCounts} ≠ nb d'ids distincts=${labelOfId.size} (double-compte).`,
            });
        }
    }

    // ── INV-6 / SPEC-6 — Répartition Focus : base de calcul == compteur Focus. ──
    // Si des mails Focus comptés ne participent pas à la répartition (null en fetch, aiDetails absent…),
    // la base diverge du compteur → répartition faussée.
    if (
        options.focusRepartitionBaseCount !== undefined &&
        surfaces.navFocus !== undefined
    ) {
        if (options.focusRepartitionBaseCount !== surfaces.navFocus) {
            violations.push({
                code: "INV-6",
                message:
                    `Répartition Focus calculée sur ${options.focusRepartitionBaseCount} mail(s) ` +
                    `mais compteur Focus=${surfaces.navFocus} (Σ segments ≠ count).`,
            });
        }
    }

    // ── INV-7 / SPEC-7 — Pas de flip-flop puis figé sur une valeur périmée. ──
    if (
        options.focusEmitHistory &&
        options.expectedConvergedFocus !== undefined
    ) {
        const history = options.focusEmitHistory;
        const last = history[history.length - 1];
        // A émis plusieurs valeurs différentes ?
        const distinct = new Set(history);
        if (distinct.size > 1 && last !== options.expectedConvergedFocus) {
            violations.push({
                code: "INV-7",
                message:
                    `Flip-flop Focus : valeurs émises [${history.join(", ")}] puis figé sur ${last} ` +
                    `≠ état convergé ${options.expectedConvergedFocus}.`,
            });
        }
    }

    // ── INV-3 / SPEC-8 — Aucune valeur incohérente (négatif / NaN / non-entier). ──
    {
        const named: [string, number | undefined][] = [
            ["welcomeX", surfaces.welcomeX],
            ["welcomeY", surfaces.welcomeY],
            ["navInbox", surfaces.navInbox],
            ["navFocus", surfaces.navFocus],
            ["dataCardFocus", surfaces.dataCardFocus],
            ...Object.entries(surfaces.navSmartLabels),
        ];
        for (const [name, value] of named) {
            if (value !== undefined && !isNonNegativeInt(value)) {
                violations.push({
                    code: "SPEC-8",
                    message: `Compteur incohérent (${name}=${value}) : négatif / non-entier / NaN interdit.`,
                });
            }
        }
    }

    return violations;
}

/**
 * Correspondance INV-* → warning `W-*` (doc `07_LOGS_INSTRUMENTATION.md`), pour préfixer les
 * `logger.warn` de recette avec le code que le testeur retrouve dans la grille `12_RECETTE`.
 */
export const INVARIANT_TO_WARNING: Record<string, string> = {
    "INV-1": "W-1",
    "INV-5": "W-7",
    "INV-4": "W-4",
    "INV-6": "W-5",
    "INV-7": "W-6",
    "SPEC-8": "W-9",
};
